import { plural } from '@cian/utils/lib/shared/plural';
import * as React from 'react';

import {
  IAgentReviewsSchema,
  IAgentSchema as IAgentSchemaOld,
  INewObjectBuilderSchema,
  IOfferSchema as IOfferSchemaOld,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { Avatar } from '../../../../common/components/avatar';
import { Link } from '../../../../common/components/link';
import { LinkWrapper } from '../../../../common/components/link_wrapper';
import { ApprovalContainer } from '../../../../containers/ApprovalContainer';
import { IAgentSchema } from '../../../../repositories/offer-card/entities/agent/AgentSchema';
import { IUserLevelInfoSchema } from '../../../../repositories/offer-card/entities/agent/UserLevelInfoSchema';
import { IOfferSchema } from '../../../../repositories/offer-card/entities/offer/OfferSchema';
import {
  getAgentName,
  getAuthorLabel,
  isAgentBrandShown,
  isBroker,
  isFromBuilder,
  isHomeowner,
  isHonest,
  isOfficialSeller,
  isPro,
} from '../../../../utils/agent';
import { CommercialOwner } from '../../../../utils/agent/CommercialOwner';
import { createAgentIdName } from '../../../../utils/createAgentIdName';
import { getNewbuildingsListQueryStringUrl } from '../../../../utils/formatters';
import { getAgentAvatar } from '../../../../utils/getAgentAvatar';
import { getAgentBrand } from '../../../../utils/getAgentBrand';
import { getExperienceText } from '../../../../utils/getExperienceText';
import { getFullProfileLink } from '../../../../utils/getFullProfileLink';
import { isAvatarAgentRoundness } from '../../../../utils/isAvatarAgentRoundness';
import { isFakeSubAgent } from '../../../../utils/isFakeSubAgent';
import { isManagementCompany } from '../../../../utils/isManagementCompany';
import { isRentDepartment } from '../../../../utils/isRentDepartment';
import { RosreestrVerified } from '../../../RosreestrVerified';
import { AgentPreviewContainer } from '../../../agent_preview';
import { AgentStats } from '../../../agent_stats';
import { getCardType, trackGoToCard } from '../../tracking';
import { HonestAsideBrand } from '../HonestAsideBrand';
import * as styles from './index.css';

export interface IAuthorAsideBrandProps {
  agent: IAgentSchemaOld | null;
  agentReviews?: IAgentReviewsSchema | null;
  companyFoundationTime: string | null;
  developer: INewObjectBuilderSchema | null;
  offer: IOfferSchemaOld;
  showCountrysideBuilderBadge: boolean;
  agentLevelInfo?: IUserLevelInfoSchema | null;
}

export class AuthorAsideBrand extends React.PureComponent<IAuthorAsideBrandProps> {
  public render() {
    const {
      agent,
      agentReviews,
      developer,
      offer,
      companyFoundationTime,
      showCountrysideBuilderBadge,
      agentLevelInfo,
    } = this.props;

    if (!agent) {
      return null;
    }

    const { isCianPartner, isDeveloper, isSubAgent } = agent;
    const isAgentPublished = agent.status === 'published';

    const isProAgent = isPro(agent);
    const isHonestAgent = isHonest(agent);
    const isBrokerAgent = isBroker(agent);
    const isAgentWithCompanyName = isFakeSubAgent(agent, offer);

    const profileLink = getFullProfileLink(agent, offer, developer);

    const avatarHandler = profileLink ? this.handlerAgentAvatarClick : undefined;
    const nameHandler = profileLink ? this.handleAgentNameClick : undefined;
    const name = isSubAgent ? agent.companyName : getAgentName(agent, offer);
    const experienceText = getExperienceText(agent, offer);
    const agentBrand = getAgentBrand(agent, offer);
    const isFromBuilderOffer = isFromBuilder(offer);
    const agentId = createAgentIdName(agent);
    const avatar = getAgentAvatar(agent);
    const label = getAuthorLabel(offer, showCountrysideBuilderBadge);
    const isStatsVisible =
      isBrokerAgent || isAgentWithCompanyName || isRentDepartment(agent) || isManagementCompany(agent);

    const isAvatarRoundness = isAvatarAgentRoundness(offer, agent);

    const isAgentBrandVisible = isAgentBrandShown(agent, offer);

    const commercialOwner = CommercialOwner.getInstanceIfIsCommercial(offer as IOfferSchema, agent as IAgentSchema);

    return (
      <>
        <div className={styles['container']}>
          <div className={styles['content']}>
            <HonestAsideBrand
              isHonestAgent={isHonestAgent}
              isProAgent={isProAgent}
              agent={agent}
              offer={offer}
              label={label}
              isCianPartner={isCianPartner}
              agentLevelInfo={agentLevelInfo}
              profileLink={profileLink}
            />
            {!agentLevelInfo && (
              <div className={styles['link-wrapper']}>
                {isAgentPublished ? (
                  <LinkWrapper
                    target="_blank"
                    href={profileLink}
                    onClick={nameHandler}
                    className={styles['title-link']}
                  >
                    <h2 className={styles['title']}>{agent?.bank || name || agentId}</h2>
                  </LinkWrapper>
                ) : (
                  <h2 className={styles['title']}>{agent?.bank || name || agentId}</h2>
                )}
              </div>
            )}
            <ApprovalContainer />
            {commercialOwner?.shield.showRosreestrVerified && (
              <div className={styles['rosreestr-verified-wrapper']}>
                <RosreestrVerified />
              </div>
            )}
            {!commercialOwner?.isLandlord &&
              isAgentBrandVisible &&
              !agent?.bank &&
              agentBrand &&
              !showCountrysideBuilderBadge && <div className={styles['prop']}>{agentBrand}</div>}
            {agent?.bank && <div className={styles['prop']}>Банк</div>}
            {isFromBuilder(offer) && companyFoundationTime && (
              <div className={styles['prop']}>Год основания: {companyFoundationTime}</div>
            )}
            {isOfficialSeller(offer) && experienceText && <div className={styles['prop']}>{experienceText}</div>}
            {isFromBuilderOffer && developer && developer.stats && (
              <ul className={styles['company']}>
                {developer.stats.done && developer.stats.done.qs && developer.stats.done.text ? (
                  <li className={styles['company-item']}>
                    <Link
                      href={getNewbuildingsListQueryStringUrl(developer.stats.done.qs)}
                      target="_blank"
                      onClick={this.handleBuildingDoneClick}
                    >
                      Сдано {developer.stats.done.text}
                    </Link>
                  </li>
                ) : (
                  <li className={styles['company-item']}>
                    <span>Нет сданных ЖК</span>
                  </li>
                )}
                {isDeveloper &&
                developer.stats.progress &&
                developer.stats.progress.qs &&
                developer.stats.progress.text ? (
                  <li className={styles['company-item']}>
                    <Link
                      href={getNewbuildingsListQueryStringUrl(developer.stats.progress.qs)}
                      target="_blank"
                      onClick={this.handleBuildingProgressClick}
                    >
                      Строится {developer.stats.progress.text}
                    </Link>
                  </li>
                ) : (
                  <li className={styles['company-item']}>
                    <span>Нет строящихся ЖК</span>
                  </li>
                )}
              </ul>
            )}
            {isStatsVisible && (
              <AgentStats uniqueKey="broker-stats" agentReviews={agentReviews} link={`/agents/${agent.id}`} />
            )}
            {(!isDeveloper || isAgentWithCompanyName) && experienceText && (
              <div className={styles['prop']}>{experienceText}</div>
            )}
            {(!isDeveloper || isAgentWithCompanyName) &&
              !!agent.offersCount &&
              agent.offersCount > 1 &&
              agent.offersLink && (
                <div>
                  <Link target="_blank" href={agent.offersLink} onClick={this.handleQtyLinkClick}>
                    Ещё {agent.offersCount - 1}&nbsp;
                    {plural(agent.offersCount - 1, ['объект', 'объекта', 'объектов'])}
                  </Link>
                </div>
              )}
            {isFromBuilderOffer && profileLink && (
              <div>
                <Link target="_blank" href={profileLink} onClick={this.handleMoreLinkClick}>
                  Подробнее о застройщике
                </Link>
              </div>
            )}
          </div>
          <LinkWrapper
            target="_blank"
            notRemoveWrapper
            href={isAgentPublished ? profileLink : undefined}
            onClick={isAgentPublished ? avatarHandler : undefined}
            className={styles['avatar-wrapper']}
          >
            <Avatar
              className={styles['avatar']}
              url={avatar}
              isRoundness={isAvatarRoundness}
              character={(!isHomeowner(offer) && name && name[0]) || ''}
            />
          </LinkWrapper>
        </div>
        {isSubAgent && (
          <div className={styles['preview']}>
            <AgentPreviewContainer onClick={this.handleAgentPreviewClick} />
          </div>
        )}
      </>
    );
  }

  private handleBuildingProgressClick = () =>
    trackGoToCard(getCardType(this.props.offer), 'in_progress_JK', 'floating_block');
  private handleBuildingDoneClick = () => trackGoToCard(getCardType(this.props.offer), 'ready_JK', 'floating_block');
  private handleQtyLinkClick = () => trackGoToCard(getCardType(this.props.offer), 'Qty', 'floating_block');
  private handleMoreLinkClick = () => trackGoToCard(getCardType(this.props.offer), 'more', 'floating_block');
  private handleAgentPreviewClick = () => trackGoToCard(getCardType(this.props.offer), 'sub', 'floating_block');
  private handlerAgentAvatarClick = () => trackGoToCard(getCardType(this.props.offer), 'ava', 'floating_block');
  private handleAgentNameClick = () => trackGoToCard(getCardType(this.props.offer), 'name', 'floating_block');
}
